/**
 * Needs you, worked top to bottom: what needs a person on the case — every
 * open ask, in its section (the API places each: `Intervention.section`),
 * and the live Word draft's open review issues (Draft review) — in the order
 * the sections are worked, each grouped by subject. Pure, so the list, the
 * keys, the queue's preview and the tests share one order.
 */

import {
  SECTIONS,
  type ChecklistItem,
  type Intervention,
  type ReviewIssue,
  type Section,
  type WorkbenchDocument,
} from '@/types/workbench';

export type NeedsYouItem =
  | {
      kind: 'ask';
      id: string;
      section: Section;
      subjectId: string | null;
      ask: Intervention;
    }
  | {
      kind: 'issue';
      id: string;
      section: 'draft_review';
      subjectId: null;
      issue: ReviewIssue;
    };

export interface NeedsYouGroup {
  key: string;
  /** Whose items they are; null for the case's own (and Draft review's). */
  subjectId: string | null;
  items: NeedsYouItem[];
}

export interface NeedsYouSection {
  section: Section;
  count: number;
  groups: NeedsYouGroup[];
}

/** The checklist item an ask is about, by its target. */
export function askChecklistItem(ask: Intervention): string | null {
  const id = ask.target.checklist_item_id;
  return typeof id === 'string' ? id : null;
}

/** The record an ask is about (an identity question's). */
export function askRecord(ask: Intervention): string | null {
  const id = ask.target.evidence_item_id;
  return typeof id === 'string' ? id : null;
}

/** Whose an ask is: its target's subject, else its checklist item's. */
export function askSubject(
  ask: Intervention,
  checklist: ReadonlyMap<string, ChecklistItem>
): string | null {
  const subject = ask.target.subject_id;
  if (typeof subject === 'string' && subject) {
    return subject;
  }
  const item = askChecklistItem(ask);
  return item ? (checklist.get(item)?.subjectId ?? null) : null;
}

/** The live Word draft — the one Needs you lists the issues of: the header's
 * (`needs_you.draft_id`), by its workbench key. */
export function liveDraftKey(
  draftId: string | null,
  documents: WorkbenchDocument[] | null
): string | null {
  if (!draftId) {
    return null;
  }
  return documents?.find((d) => d.kind === 'draft' && d.id === draftId)?.workbenchKey ?? null;
}

/** Every item that needs a person: the open asks in their sections, and the
 * live draft's open review issues. */
export function needsYouItems(
  asks: Intervention[],
  issues: ReviewIssue[] | null,
  draftKey: string | null,
  checklist: ChecklistItem[] | null
): NeedsYouItem[] {
  const items_ = new Map((checklist ?? []).map((c) => [c.id, c]));
  const items: NeedsYouItem[] = [];
  for (const ask of asks) {
    if (ask.status === 'open') {
      items.push({
        kind: 'ask',
        id: ask.id,
        section: ask.section,
        subjectId: askSubject(ask, items_),
        ask,
      });
    }
  }
  if (draftKey) {
    for (const issue of issues ?? []) {
      if (issue.status === 'open' && issue.workbenchKey === draftKey) {
        items.push({
          kind: 'issue',
          id: issue.id,
          section: 'draft_review',
          subjectId: null,
          issue,
        });
      }
    }
  }
  return items;
}

function createdAt(item: NeedsYouItem): string {
  return item.kind === 'ask' ? item.ask.createdAt : item.issue.createdAt;
}

/** A draft's issues in the order they're worked: errors first, then by
 * page, then in the order they were raised. */
export function issueOrder(
  a: Pick<ReviewIssue, 'severity' | 'page' | 'createdAt'>,
  b: Pick<ReviewIssue, 'severity' | 'page' | 'createdAt'>
): number {
  const severity = Number(b.severity === 'error') - Number(a.severity === 'error');
  const page = (a.page ?? Number.MAX_SAFE_INTEGER) - (b.page ?? Number.MAX_SAFE_INTEGER);
  return severity || page || a.createdAt.localeCompare(b.createdAt);
}

/** Draft review's order: the draft's own asks first, then its issues. */
function draftOrder(a: NeedsYouItem, b: NeedsYouItem): number {
  if (a.kind !== b.kind) {
    return a.kind === 'ask' ? -1 : 1;
  }
  if (a.kind === 'issue' && b.kind === 'issue') {
    return issueOrder(a.issue, b.issue);
  }
  return createdAt(a).localeCompare(createdAt(b));
}

/** The sections in the order they're worked, empty ones left out; each
 * grouped by subject in the case's order (`subjectIds`), the case's own
 * last; within a group, in the order raised. Draft review is one group. */
export function needsYouSections(items: NeedsYouItem[], subjectIds: string[]): NeedsYouSection[] {
  const rank = new Map(subjectIds.map((id, i) => [id, i]));
  const sections: NeedsYouSection[] = [];
  for (const section of SECTIONS) {
    const inSection = items.filter((item) => item.section === section);
    if (inSection.length === 0) {
      continue;
    }
    if (section === 'draft_review') {
      sections.push({
        section,
        count: inSection.length,
        groups: [{ key: section, subjectId: null, items: [...inSection].sort(draftOrder) }],
      });
      continue;
    }
    const groups = new Map<string, NeedsYouGroup>();
    for (const item of inSection) {
      const key = item.subjectId ?? 'case';
      const group = groups.get(key) ?? { key, subjectId: item.subjectId, items: [] };
      group.items.push(item);
      groups.set(key, group);
    }
    for (const group of groups.values()) {
      group.items.sort((a, b) => createdAt(a).localeCompare(createdAt(b)));
    }
    sections.push({
      section,
      count: inSection.length,
      groups: [...groups.values()].sort(
        (a, b) =>
          (a.subjectId === null ? Infinity : (rank.get(a.subjectId) ?? subjectIds.length)) -
          (b.subjectId === null ? Infinity : (rank.get(b.subjectId) ?? subjectIds.length))
      ),
    });
  }
  return sections;
}

/** The list's items, top to bottom. */
export function workOrder(sections: NeedsYouSection[]): NeedsYouItem[] {
  return sections.flatMap((s) => s.groups.flatMap((g) => g.items));
}

/** The item after one that left the list: the next one below it that's
 * still there, else the first. */
export function nextAfter(
  previous: NeedsYouItem[],
  current: NeedsYouItem[],
  leftId: string
): NeedsYouItem | null {
  const still = new Set(current.map((i) => i.id));
  const at = previous.findIndex((i) => i.id === leftId);
  const below = at === -1 ? [] : previous.slice(at + 1);
  const next = below.find((i) => still.has(i.id));
  return (next && current.find((i) => i.id === next.id)) ?? current[0] ?? null;
}

/** What Needs you keeps across a trip to another view, which unmounts it:
 * the items a person closed — answered in it, or an issue closed in the
 * draft — so none is said to be settled by an agent, and the list as it last
 * stood, so what an agent settled meanwhile is said on the way back. One per
 * case. */
export interface NeedsYouMemory {
  answered: Set<string>;
  previous: NeedsYouItem[];
}

export function needsYouMemory(): NeedsYouMemory {
  return { answered: new Set(), previous: [] };
}

/** What left the list without the person's own answer — settled by an
 * agent while they worked — by section. */
export function settledWhileWorking(
  previous: NeedsYouItem[],
  current: NeedsYouItem[],
  answered: ReadonlySet<string>
): Partial<Record<Section, number>> {
  const still = new Set(current.map((i) => i.id));
  const settled: Partial<Record<Section, number>> = {};
  for (const item of previous) {
    if (!still.has(item.id) && !answered.has(item.id)) {
      settled[item.section] = (settled[item.section] ?? 0) + 1;
    }
  }
  return settled;
}
